import React from 'react';
import { motion } from 'framer-motion';
import { Leaf, Recycle, Globe, Droplets, Sun, Truck, ArrowRight, TreePine } from 'lucide-react';
import { AnimatedCounter } from '../ui/AnimatedCounter';
import { EnhancedScrollReveal } from '../common/EnhancedScrollReveal';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
export function SustainabilityImpact() {
  return <section className="py-24 lg:py-32 bg-[#FAF9F6] relative overflow-hidden" data-id="element-3488">
      {/* Nature Background Layers */}
      <div className="absolute bottom-0 left-0 w-full h-1/3 bg-gradient-to-t from-emerald-50 to-transparent pointer-events-none" data-id="element-3489" />
      <div className="absolute -left-20 top-1/4 w-96 h-96 bg-emerald-100/30 rounded-full blur-[100px]" data-id="element-3490" />
      <div className="absolute -right-20 bottom-1/4 w-96 h-96 bg-blue-100/30 rounded-full blur-[100px]" data-id="element-3491" />

      <div className="container-custom max-w-7xl mx-auto relative z-10" data-id="element-3492">
        <div className="flex flex-col lg:flex-row justify-between items-end mb-20 gap-8" data-id="element-3493">
          <div className="max-w-2xl" data-id="element-3494">
            <EnhancedScrollReveal data-id="element-3495">
              <Badge variant="secondary" className="mb-6 bg-emerald-100 text-emerald-700 border-emerald-200" data-id="element-3496">
                <Globe className="w-3 h-3 mr-1" data-id="element-3497" /> Compromiso Planeta
              </Badge>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-charcoal-900 mb-6 leading-tight" data-id="element-3498">
                Huella positiva, <br data-id="element-3499" />
                <span className="text-emerald-600" data-id="element-3500">pata a pata</span>
              </h2>
              <p className="text-lg text-charcoal-600 leading-relaxed font-light" data-id="element-3501">
                La industria de mascotas tradicional genera toneladas de
                residuos plásticos. Nosotros elegimos un camino diferente:
                circular, local y consciente.
              </p>
            </EnhancedScrollReveal>
          </div>

          <EnhancedScrollReveal delay={0.2} data-id="element-3502">
            <Button variant="outline" className="rounded-full border-emerald-200 text-emerald-800 hover:bg-emerald-50" data-id="element-3503">
              Ver Informe de Sostenibilidad{' '}
              <ArrowRight className="ml-2 w-4 h-4" data-id="element-3504" />
            </Button>
          </EnhancedScrollReveal>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 mb-20" data-id="element-3505">
          {[{
          icon: Leaf,
          val: 80,
          suffix: '%',
          title: 'Menos CO2',
          desc: 'Nuestra cadena de suministro local reduce drásticamente las emisiones de transporte comparado con marcas importadas.',
          color: 'text-emerald-600',
          bg: 'bg-emerald-50'
        }, {
          icon: Recycle,
          val: 100,
          suffix: '%',
          title: 'Circularidad',
          desc: 'Todos nuestros envases son reciclables o compostables. Cero plásticos de un solo uso en nuestros envíos.',
          color: 'text-blue-600',
          bg: 'bg-blue-50'
        }, {
          icon: Sun,
          val: 75,
          suffix: '%',
          title: 'Energía Verde',
          desc: 'Nuestra cocina central opera mayoritariamente con energía solar y sistemas de recuperación de calor.',
          color: 'text-amber-600',
          bg: 'bg-amber-50'
        }].map((item, i) => <EnhancedScrollReveal key={i} delay={i * 0.15} data-id="element-3506">
              <div className="bg-white rounded-[2.5rem] p-8 border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-300 group h-full" data-id="element-3507">
                <div className={`w-16 h-16 rounded-2xl ${item.bg} flex items-center justify-center mb-8 group-hover:scale-110 transition-transform duration-500`} data-id="element-3508">
                  <item.icon className={`w-8 h-8 ${item.color}`} data-id="element-3509" />
                </div>
                <div className="text-5xl font-serif font-bold text-charcoal-900 mb-4 flex items-baseline" data-id="element-3510">
                  <AnimatedCounter value={item.val} data-id="element-3511" />
                  <span className={`text-3xl ${item.color}`} data-id="element-3512">
                    {item.suffix}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-charcoal-900 mb-4" data-id="element-3513">
                  {item.title}
                </h3>
                <p className="text-charcoal-600 leading-relaxed text-sm" data-id="element-3514">
                  {item.desc}
                </p>
              </div>
            </EnhancedScrollReveal>)}
        </div>

        {/* Impact Calculator Banner */}
        <EnhancedScrollReveal delay={0.4} data-id="element-3515">
          <div className="bg-gradient-to-r from-emerald-900 to-emerald-800 rounded-[3rem] p-8 md:p-16 text-white relative overflow-hidden text-center" data-id="element-3516">
            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/wood-pattern.png')] opacity-10" data-id="element-3517" />
            <div className="absolute top-0 left-0 w-64 h-64 bg-white/10 rounded-full blur-[80px] -translate-x-1/2 -translate-y-1/2" data-id="element-3518" />

            <div className="relative z-10 max-w-3xl mx-auto" data-id="element-3519">
              <TreePine className="w-12 h-12 mx-auto mb-6 text-emerald-300" data-id="element-3520" />
              <h3 className="text-3xl md:text-4xl font-serif font-bold mb-6" data-id="element-3521">
                Tu cambio cuenta
              </h3>
              <p className="text-emerald-100 text-lg mb-10" data-id="element-3522">
                Al cambiar a Petiora, una mascota promedio ahorra el equivalente
                a{' '}
                <span className="font-bold text-white" data-id="element-3523">
                  300 botellas de plástico
                </span>{' '}
                y reduce su huella de carbono en{' '}
                <span className="font-bold text-white" data-id="element-3524">150kg de CO2</span> al
                año.
              </p>
              <Button size="xl" variant="white" className="rounded-full px-10 text-emerald-900 hover:bg-emerald-50" data-id="element-3525">
                Únete al Movimiento
              </Button>
            </div>
          </div>
        </EnhancedScrollReveal>
      </div>
    </section>;
}